import logger from "../../utils/logger";
import { supabase } from './client';
import { compressImage, validateImageFile } from '../../utils/imageCompression';

const PROPERTY_PHOTOS_BUCKET = 'property-photos';
const PROPERTY_DOCUMENTS_BUCKET = 'property-documents';

/**
 * Upload głównego zdjęcia nieruchomości
 */
export async function uploadPropertyPhoto(file, propertyId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Użytkownik niezalogowany');

  // Walidacja
  const validation = validateImageFile(file);
  if (!validation.valid) throw new Error(validation.error);

  // Kompresja
  const compressed = await compressImage(file, {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
  });

  // Ścieżka: userId/propertyId/primary.jpg
  const ext = compressed.name.split('.').pop();
  const path = `${user.id}/${propertyId}/primary.${ext}`;

  // Upload z upsert (zastępuje stare zdjęcie)
  const { data, error } = await supabase.storage
    .from(PROPERTY_PHOTOS_BUCKET)
    .upload(path, compressed, {
      contentType: compressed.type,
      upsert: true,
    });

  if (error) throw error;

  // Zwróć podpisany URL
  const { data: signedUrl } = await supabase.storage
    .from(PROPERTY_PHOTOS_BUCKET)
    .createSignedUrl(path, 3600);

  return {
    path,
    url: signedUrl.signedUrl,
    size: compressed.size,
  };
}

/**
 * Upload załącznika (skan umowy)
 */
export async function uploadPropertyDocument(file, propertyId, docType = 'contract') {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Użytkownik niezalogowany');

  // Walidacja rozmiaru (max 10MB dla PDF)
  if (file.size > 10 * 1024 * 1024) {
    throw new Error('Plik jest za duży. Maksymalny rozmiar to 10MB.');
  }

  // Walidacja typu
  const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
  if (!allowedTypes.includes(file.type)) {
    throw new Error('Nieprawidłowy typ pliku. Dozwolone: PDF, JPG, PNG');
  }

  const ext = file.name.split('.').pop();
  const timestamp = Date.now();
  const path = `${user.id}/${propertyId}/${docType}_${timestamp}.${ext}`;

  const { data, error } = await supabase.storage
    .from(PROPERTY_DOCUMENTS_BUCKET)
    .upload(path, file, {
      contentType: file.type,
    });

  if (error) throw error;

  const { data: signedUrl } = await supabase.storage
    .from(PROPERTY_DOCUMENTS_BUCKET)
    .createSignedUrl(path, 3600);

  return {
    path,
    url: signedUrl.signedUrl,
    fileName: file.name,
    size: file.size,
  };
}

/**
 * Pobranie podpisanego URL zdjęcia
 */
export async function getPropertyPhotoUrl(path, expiresIn = 3600) {
  if (!path) return null;

  logger.log('[propertyStorage] getPropertyPhotoUrl - start', { path });
  const startTime = Date.now();

  try {
    const { data, error } = await supabase.storage
      .from(PROPERTY_PHOTOS_BUCKET)
      .createSignedUrl(path, expiresIn);

    if (error) {
      // Silent fallback for "not found" errors (orphaned paths in DB)
      if (error.message?.includes('Object not found') || error.statusCode === '404') {
        logger.log('[propertyStorage] Photo not found (orphaned path):', path);
      } else {
        console.error('[propertyStorage] Error getting property photo URL:', error);
      }
      return null;
    }

    logger.log(`[propertyStorage] getPropertyPhotoUrl - completed in ${Date.now() - startTime}ms`);
    return data.signedUrl;
  } catch (err) {
    // Silent fallback for common errors
    if (err.message?.includes('Object not found')) {
      logger.log('[propertyStorage] Photo not found (exception):', path);
    } else {
      console.error('[propertyStorage] getPropertyPhotoUrl - exception:', err);
    }
    return null;
  }
}

/**
 * Pobranie podpisanego URL dokumentu
 */
export async function getPropertyDocumentUrl(path, expiresIn = 3600) {
  if (!path) return null;

  logger.log('[propertyStorage] getPropertyDocumentUrl - start', { path });
  const startTime = Date.now();

  try {
    const { data, error } = await supabase.storage
      .from(PROPERTY_DOCUMENTS_BUCKET)
      .createSignedUrl(path, expiresIn);

    if (error) {
      console.error('[propertyStorage] Error getting property document URL:', error);
      return null;
    }

    logger.log(`[propertyStorage] getPropertyDocumentUrl - completed in ${Date.now() - startTime}ms`);
    return data.signedUrl;
  } catch (err) {
    console.error('[propertyStorage] getPropertyDocumentUrl - exception:', err);
    return null;
  }
}

/**
 * Usunięcie zdjęcia nieruchomości
 */
export async function deletePropertyPhoto(path) {
  const { error } = await supabase.storage
    .from(PROPERTY_PHOTOS_BUCKET)
    .remove([path]);

  if (error) throw error;
}

/**
 * Usunięcie dokumentu
 */
export async function deletePropertyDocument(path) {
  const { error } = await supabase.storage
    .from(PROPERTY_DOCUMENTS_BUCKET)
    .remove([path]);

  if (error) throw error;
}
